import { ChangeDetectorRef, Component, ChangeDetectionStrategy } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { ExerciseDbService, Exercise, ExerciseTextLanguage, isStrengthStandardRelative } from 'services/exercise-db.service';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { equipmentTemplatesEU } from 'pages/equipment/equipment-templates-eu';
import { localizeEquipmentTemplates } from 'pages/equipment/equipment-templates';
import { Equipment } from '@cf-shared/data-model/equipment';
import { IEditExercisePage } from 'pages/edit-exercise/edit-exercise-interface';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { DocWatcher } from 'services/database-wrapper';

interface ExerciseInfo {
  exercise: Exercise;
  name: string;
  bwPc?: string;
  rmTest?: string;
  myoreps?: string;
  template?: string;
  standard?: string;
};

@Component({
    selector: 'page-admin-exercise-list',
    templateUrl: 'admin-exercise-list.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class AdminExerciseListPage extends CsPage<{}> {
  exercises: ExerciseInfo[] = [];
  filteredExercises: ExerciseInfo[] = [];
  equipmentTemplates: Equipment[] = [];
  loading: boolean = true;
  searchText: string = '';

  constructor(
    public navCtrl: NavController,
    protected exerciseDb: ExerciseDbService,
    protected auth: AuthService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Dev);
  }
  async ionViewWillEnter() {
    this.loading = true;
    // pin English — this list is the entry point to the admin exercise editor,
    // and English is the authoring locale for curated exercise text (#4210)
    let allExercises = await this.exerciseDb.getAllExercisesAsync(undefined, ExerciseTextLanguage.Authoring);
    // the EU set is used on purpose here — this list only needs one name per
    // equipment id, and the two sets share every id.
    //
    // these names are LOCALIZED even though the exercise text above is pinned to
    // English. that is deliberate, not an oversight: the pin exists because an
    // admin editing curated exercise text must see and save the authoring copy,
    // whereas an equipment template name is an ordinary UI label that this page
    // only displays — it stores `equipmentTemplateId`, never the name. so a
    // non-English admin correctly reads this column in their own language.
    this.equipmentTemplates = localizeEquipmentTemplates(equipmentTemplatesEU);
    // withDocWatcherAsync, not a hand-written create/destroy: the name lookups inside can
    // throw when the database could not be read at all, and a `destroy()` on the line after
    // them would never run, leaving this watcher registered for the rest of the session.
    this.exercises = await this.exerciseDb.database.withDocWatcherAsync(watcher =>
      Promise.all(allExercises.map(async e => await this.formatExerciseAsync(e, watcher))));
    this.applyFilter();
    this.loading = false;
    this.cdRef.markForCheck();
  }

  handleSearchChanged() {
    this.applyFilter();
    this.cdRef.markForCheck();
  }

  applyFilter() {
    const searchLower = this.searchText.toLowerCase().trim();
    if (!searchLower) {
      this.filteredExercises = this.exercises;
    } else {
      this.filteredExercises = this.exercises.filter(e =>
        e.name.toLowerCase().includes(searchLower)
      );
    }
  }

  getEquipmentName(id: string) {
    let wc = this.equipmentTemplates.find(wc => wc.id == id);
    return wc ? wc.name : 'NO WEIGHTS';
  }

  async formatExerciseAsync(e: Exercise, watcher: DocWatcher): Promise<ExerciseInfo> {
    let res: ExerciseInfo = {
      exercise: e,
      name: e.name,
      bwPc: e.bw_pc ? `${e.bw_pc}% BW` : '',
      rmTest: e.avoidRMTesting ? '' : 'RM Test',
      myoreps: e.allowMyoReps ? 'MyoReps' : '',
      template: this.getEquipmentName(e.equipmentTemplateId),
    };
    if (e.standard) {
      if (isStrengthStandardRelative(e.standard)) {
        let relativeExerciseName = await this.exerciseDb.getExerciseNameByIdAsync(
          e.standard.idReferenceExercise, watcher, ExerciseTextLanguage.Authoring);
        res.standard = `Std: ${relativeExerciseName} x ${e.standard.factor1RM_pc}`;
      } else {
        res.standard = 'Std: Absolute';
      }
    }

    return res;
  }

  newExercise() {
    void this.navCtrl.pushAsync(IEditExercisePage, { id: '', mode: 'admin' });
  }
  editExercise(e: ExerciseInfo) {
    void this.navCtrl.pushAsync(IEditExercisePage, { id: e.exercise._id, mode: 'admin' });
  }
}
